import styled from "styled-components"

import { CircularProgress, Text } from "@minuthq/meatball-ui-react"

import { useGetUser } from "src/data/user/hooks/useGetUser"
import { usePatchUserNotificationSettings } from "src/data/user/queries/userQueries"
import {
  TPushNotificationChannel,
  TPushNotificationMode,
  TUserEventNotification,
} from "src/data/user/types/userTypes"
import { useTranslate } from "src/i18n/useTranslate"
import { DropdownSelect } from "src/ui/DropdownSelect/DropdownSelect"
import { MTooltip } from "src/ui/MTooltip/MTooltip"

type TNotificationModeOption = {
  label: React.ReactNode
  value: TPushNotificationMode
}
type TModeOptionTranslation = Record<
  TPushNotificationMode,
  { title: string; description: string | null }
>

export function PushModeSelect({
  notification,
  channel,
}: {
  notification: TUserEventNotification
  channel: TPushNotificationChannel
}) {
  const { tPlaceholder } = useTranslate()
  const user = useGetUser()

  const patchUserNotificationSettings = usePatchUserNotificationSettings({
    userId: user.user_id,
  })

  const options: TNotificationModeOption[] = [
    {
      label: <OptionLabel {...getOptionTranslations("off")} />,
      value: "off",
    },
    {
      label: <OptionLabel {...getOptionTranslations("on")} />,
      value: "on",
    },
    {
      label: <OptionLabel {...getOptionTranslations("critical")} />,
      value: "critical",
    },
  ]

  function handleChange(newMode: TPushNotificationMode) {
    const updatedChannels = notification.channels
      .filter((channel) => channel.editable)
      .map((channel) =>
        channel.type !== "push"
          ? { type: channel.type, mode: channel.mode }
          : { type: "push" as const, mode: newMode }
      )

    patchUserNotificationSettings.mutate({
      path: { user_id: user.user_id },
      body: {
        events: [{ type: notification.type, channels: updatedChannels }],
      },
    })
  }

  function getOptionTranslations(mode: TPushNotificationMode) {
    const translations: TModeOptionTranslation = {
      on: {
        title: tPlaceholder("Push"),
        description: tPlaceholder("Send a notification to your devices"),
      },
      off: {
        title: tPlaceholder("Off"),
        description: null,
      },
      critical: {
        title: tPlaceholder("Critical push"),
        description: tPlaceholder(
          "Send a high-priority notification that bypasses the device’s mute or Do Not Disturb settings."
        ),
      },
    }

    return translations[mode]
  }

  const isLoading = patchUserNotificationSettings.isLoading

  return (
    <Box>
      <MTooltip title={!channel.editable ? tPlaceholder("Not available") : ""}>
        <DropdownSelect<TPushNotificationMode, TNotificationModeOption>
          options={options}
          selectedValue={channel.mode}
          label={
            isLoading ? (
              <CircularProgress size="18px" />
            ) : (
              getOptionTranslations(channel.mode).title
            )
          }
          onChange={({ option }) => {
            handleChange(option.value)
          }}
          size="small"
          disabled={!channel.editable || isLoading}
          showCheckmark={false}
          loading={isLoading}
        />
      </MTooltip>
    </Box>
  )
}

function OptionLabel({
  title,
  description,
}: {
  title: string
  description: string | null
}) {
  return (
    <Box>
      <Text variant="body">{title}</Text>
      {description && (
        <Text variant="bodyS" color="secondary">
          {description}
        </Text>
      )}
    </Box>
  )
}

const Box = styled.div`
  width: max-content;
`
